import React, { useState, useEffect, useCallback } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Tooltip,
  ReferenceLine,
  Cell,
} from "recharts";
import styles from "./CustomChart.module.css";

const tooltipFormatKey = (key) =>
  String(key || "")
    .replace(/,/g, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const tooltipFormatValue = (val) => {
  if (val == null) return "-";
  const num = Number(String(val).replace(/,/g, ""));
  if (!isNaN(num) && typeof val !== "boolean") {
    return String(Math.round(num));
  }
  return String(val).replace(/,/g, "");
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload || {};

  // Flatten all fields including nested additional
  const allFields = [
    ...Object.entries(data).filter(([key]) => key !== "additional"),
    ...(data.additional ? Object.entries(data.additional) : []),
  ];

  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        p: 1.5,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        minWidth: 220,
        maxWidth: 350,
      }}
    >
      {allFields.map(([key, value]) => (
        <Box
          key={key}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            py: 0.4,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              color: "#4D5F6E",
              fontWeight: 500,
            }}
          >
            {tooltipFormatKey(key)}
          </Typography>

          <Typography
            sx={{
              fontSize: 12,
              color: "#1A2025",
              fontWeight: 600,
              textAlign: "right",
              wordBreak: "break-word",
            }}
          >
            {tooltipFormatValue(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};;
const VerticalCustomLabel = ({ x, y, width, value, valueColor }) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  const rounded = !isNaN(num) ? Math.ceil(num) : value;
  // Plain number — no thousand separators.
  const display =
    typeof rounded === "number" ? String(rounded) : String(rounded);

  return (
    <text
      x={x + width / 2}
      y={y - 6}
      textAnchor="middle"
      fontSize={12}
      fontWeight={600}
      fill={valueColor}
    >
      {display}
    </text>
  );
};

const CustomChartSkeleton = ({ count }) => (
  <Box sx={{ display: "flex", alignItems: "flex-end", gap: 14 }}>
    {Array.from({ length: count }).map((_, i) => (
      <Box
        key={i}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Skeleton
          variant="rounded"
          width={40}
          height={200 + i * 20}
          sx={{ borderRadius: 2 }}
        />
        <Skeleton variant="text" width={40} height={20} />
      </Box>
    ))}
  </Box>
);
const stripLeadingZeros = (val) => {
  const str = val == null ? "" : String(val);
  if (/^0+\d/.test(str)) return str.replace(/^0+/, "");
  return str;
};

const CustomXAxisTick = ({ x, y, payload }) => {
  const text = stripLeadingZeros(payload?.value);
  if (!text) return null;

  const maxCharsPerLine = 10;
  const lines = [];
  for (let i = 0; i < text.length; i += maxCharsPerLine) {
    lines.push(text.substring(i, i + maxCharsPerLine));
  }
  const visibleLines = lines.slice(0, 3);
  if (visibleLines.length === 3 && text.length > maxCharsPerLine * 3) {
    visibleLines[2] = visibleLines[2].slice(0, -1) + "…";
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#4D5F6E" fontSize={12} fontWeight={500}>
        {visibleLines.map((line, index) => (
          <tspan key={index} x="0" dy={index === 0 ? 12 : 14}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};
const formatKey = (key) =>
  String(key || "")
    .replace(/,/g, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const CustomChartContent = ({
  chartData = [],
  barColor = "#3B82F6",
  valueColor = "#111827",
  loading,
  name,
  value,
  threshold,
  thresholdColor = "#FF4D4F",
  aboveThresholdColor,
  belowThresholdColor,
}) => {
  if (loading) return <CustomChartSkeleton count={4} />;

  // Sanitize data — replace NaN/undefined/null values with 0
  const sanitizedData = chartData.map((item) => ({
    ...item,
    [value]: isNaN(Number(item[value])) ? 0 : Number(item[value]),
  }));

  const hasThreshold = threshold != null && !isNaN(Number(threshold));
  const aboveColor = aboveThresholdColor || thresholdColor;
  const belowColor = belowThresholdColor || barColor;

  const barMinWidth = 120;
  const minChartWidth = sanitizedData.length * barMinWidth;

  // Build legend items from data_points (name = xKey, value = yKey)
  const legendItems = [];
  if (hasThreshold) {
    legendItems.push(
      { label: `${formatKey(value)} (Above ${threshold})`, color: aboveColor },
      { label: `${formatKey(value)} (Below ${threshold})`, color: belowColor },
    );
  } else {
    if (name) {
      legendItems.push({
        label: formatKey(name),
        color: "#667F93",
        type: "axis",
      });
    }
    const yKeys = Array.isArray(value) ? value : [value];
    const barColors = Array.isArray(barColor) ? barColor : [barColor];
    yKeys.forEach((key, idx) => {
      legendItems.push({
        label: formatKey(key),
        color: barColors[idx % barColors.length],
        type: "bar",
      });
    });
  }

  return (
    <>
      <Box
        sx={{
          width: "100%",
          overflowX: minChartWidth > 800 ? "auto" : "hidden",
          overflowY: "hidden",
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            minWidth: minChartWidth,
            height: { sm: 160, md: 200, lg: 300 },
          }}
        >
          <ResponsiveContainer>
            <BarChart
              data={sanitizedData}
              barCategoryGap="25%"
              margin={{ top: 20, right: 20, left: 10, bottom: 40 }}
            >
              <CartesianGrid stroke="#E5E7EB" vertical={false} />

              <XAxis
                dataKey={name}
                interval={0}
                axisLine={false}
                tickLine={false}
                tick={<CustomXAxisTick />}
                tickMargin={16}
                angle={-30}
                textAnchor="end"
              />

              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: "#4D5F6E",
                  fontSize: 12,
                  fontWeight: 500,
                }}
                tickMargin={12}
                width={85}
              />
              <Tooltip content={<CustomTooltip xKey={name} yKey={value} />} />

              {hasThreshold && (
                <ReferenceLine
                  y={Number(threshold)}
                  stroke={thresholdColor}
                  strokeDasharray="6 4"
                  strokeWidth={2}
                  label={{
                    value: `Threshold: ${threshold}`,
                    position: "insideTopRight",
                    fill: thresholdColor,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                />
              )}

              <Bar
                dataKey={value}
                fill={barColor}
                radius={[8, 8, 0, 0]}
                barSize={40}
              >
                {hasThreshold &&
                  sanitizedData.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={
                        Number(entry[value]) >= Number(threshold)
                          ? aboveColor
                          : belowColor
                      }
                    />
                  ))}
                <LabelList
                  dataKey={value}
                  content={(props) => (
                    <VerticalCustomLabel {...props} valueColor={valueColor} />
                  )}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {/* Legend — fixed outside scroll container */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "12px 24px",
          pt: 1.5,
          pb: 0.5,
        }}
      >
        {legendItems.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {item.type === "axis" ? (
              <Box
                sx={{
                  width: 16,
                  borderTop: "2px solid #667F93",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "3px",
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
            )}
            <Box
              sx={{
                fontSize: 12,
                color: "#4D5F6E",
                fontWeight: 500,
              }}
            >
              {item.label}
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
};

const VerticalBarChart = ({
  data = [],
  barColor = "#B6F7FF",
  valueColor = "#1A2025",
  percentColor = "#667F93",
  refreshTrigger,
  name,
  value,
  threshold,
  thresholdColor,
  aboveThresholdColor,
  belowThresholdColor,
}) => {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState(data);

  useEffect(() => {
    setChartData(data);
  }, [data]);

  const handleRefresh = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      setChartData([...data]);
      setLoading(false);
    }, 1500);
  }, [data]);

  useEffect(() => {
    if (refreshTrigger) {
      handleRefresh();
    }
  }, [refreshTrigger, handleRefresh]);

  const sharedChartProps = {
    chartData,
    barColor,
    valueColor,
    percentColor,
    loading,
    name,
    value,
    threshold,
    thresholdColor,
    aboveThresholdColor,
    belowThresholdColor,
  };

  return (
    <Box
      sx={{
        background: "#fff",
        height: "100%",
      }}
    >
      <Box className={styles.graphBG}>
        <CustomChartContent {...sharedChartProps} />
      </Box>
    </Box>
  );
};

export default VerticalBarChart;